import { Graphics, Point, utils } from 'pixi.js';

import { Shared } from '../../App';
import { SplineDisplay } from '../../display/SplineDisplay';
import { Segment } from '../../utils/Segment';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { BaseEntity } from './BaseEntity';

export interface ShapeEntityParams
{
    type: string,
    precision: number,
    thickness: number,
    baseline: number,
    x: number,
    y: number,
    img: string,
    maxPrecision: number,
    colinearAngle: number,
    verts: {x: number, y: number, cx: number, cy: number}[]
    pattern: string,
    color: string
    props?: {z?: number}
}

export class ShapeEntity extends BaseEntity
{
    public spline?: SplineDisplay;
    public culling: CullingComponent;
    public poly?: Graphics;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
    }

    public build(params: ShapeEntityParams): void
    {
        const maxPrecision = params.maxPrecision || 16;
        const colinearAngle = params.colinearAngle || 0.01;
        const list = Segment.getPointsFromVerts(params.verts, false, maxPrecision, colinearAngle);
        const points = list.map((p) => new Point(p.x, p.y));

        this.updatePoly(params, points);
        this.updateSpline(params, points);

        this.x = params.x + ((this.poly?.width || 0) / 2);
        this.y = params.y + ((this.poly?.height || 0) / 2);
        this.z = params.props?.z ?? (params.color === '#cb9348' ? -0.00003 : 0);

        this.culling.updateBounds();
    }

    private updatePoly(params: ShapeEntityParams, points: Point[]): void
    {
        if (this.poly)
        {
            if (this.poly.parent) this.poly.parent.removeChild(this.poly);
            this.poly.destroy();
            this.poly = undefined;
        }

        this.poly = new Graphics();
        if (params.pattern)
        {
            this.poly.beginTextureFill({ texture: Shared.assets.getTexture(params.pattern) });
        }
        else
        {
            this.poly.beginFill(utils.string2hex(params.color || '#FF0000'));
        }
        this.poly.drawPolygon(points);
        this.poly.endFill();
        this.poly.x = -this.poly.width / 2;
        this.poly.y = -this.poly.height / 2;
        this.view.addChildAt(this.poly, 0);
    }

    private updateSpline(params: ShapeEntityParams, points: Point[]): void
    {
        const splinePoints = points.slice(0);

        if (this.spline)
        {
            if (this.spline.parent) this.spline.parent.removeChild(this.spline);
            this.spline.destroy();
            this.spline = undefined;
        }

        if (!params.img) return;

        const splineTexture = Shared.assets.getTexture(params.img);

        splinePoints.push(points[0]);

        this.spline = new SplineDisplay(splinePoints, splineTexture, {
            thickness: splineTexture.height,
            baseline: 0.5,
            textureScale: 1,
            repeatDistance: splineTexture.width * 2,
        });
        this.spline.x = -(this.poly?.width || 0) / 2;
        this.spline.y = -(this.poly?.height || 0) / 2;
        this.view.addChild(this.spline);
    }
}

export class ShapeFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'ShapeEntity';
    }

    public static create(game: Game, params: Record<string, any>): void
    {
        const ground = game.pool.get(ShapeEntity) as ShapeEntity;

        ground.build(params as ShapeEntityParams);
        game.level.addEntity(ground);
    }
}
